import { Form, Formik, FormikProps } from "formik";
import UploadPolicyContainer from "./upload-policy-container";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import MyText from "../../components/Input/MyText";
import { Box, Divider, Grid, Typography, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { PolicySavePayloadDTO } from "../../types";
import FormikDropdown from "../../components/Input/FormikDropdown";
import { FormValues, getValidationSchema } from "./health-policy-form";
import { useStateValue } from "../../util/stateProvider";
import {
    getMakeOptions,
    getModels,
    getVariants,
    policyService,
} from "../../services/api/policyServics";

type Props = {
    onSubmit: (payload: PolicySavePayloadDTO) => void;
    policyData: PolicySavePayloadDTO | null;
    isLoading: boolean;
    customer: {
        label: string;
        value: string;
    };
};

const MotorPolicyOptions = [
    {
        id: "MOTOR2W",
        name: "MOTOR2W",
    },
    {
        id: "MOTOR4W",
        name: "MOTOR4W",
    },
    {
        id: "MOTORCOMMERCIAL",
        name: "MOTORCOMMERCIAL",
    },
];

type DropdownOption = {
    id: string;
    name: string;
};

export default function MotorPolicyForm({
    onSubmit,
    policyData,
    isLoading,
    customer,
}: Props) {
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const theme = useTheme();
    const [{ user }] = useStateValue();

    let agentUuid = user?.agentUuid || null;
    const [isFetched, setIsFetched] = useState({
        insurer: false,
        make: false,
        model: false,
        variant: false,
    });
    const [insurerOptions, setInsurerOptions] = useState<DropdownOption[]>([]);

    const fetchInsurerOptions = () => {
        if (agentUuid && !isFetched.insurer) {
            policyService.getInsurerList(agentUuid).then((res) => {
                if (res && res.data.response.length) {
                    setInsurerOptions(
                        res.data.response.map((insurer) => ({
                            id: insurer.value,
                            name: insurer.label,
                        }))
                    );
                    setIsFetched((prev) => ({ ...prev, insurer: true }));
                }
            });
        }
    };

    const [makeOptions, setMakeOptions] = useState<DropdownOption[]>([]);
    const [modelOptions, setModelOptions] = useState<DropdownOption[]>([]);
    const [variantOptions, setVariantOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        if (agentUuid) {
            getMakeOptions(agentUuid).then((res) => {
                if (res && res.length) {
                    setMakeOptions(
                        res.map((make) => ({
                            id: make.value,
                            name: make.label,
                        }))
                    );
                    setModelOptions([]);
                    setVariantOptions([]);
                }
                // setIsFetched((prev) => ({ ...prev, make: true }));
            });
        }
    }, [agentUuid]);

    useEffect(() => {
        if (policyData?.motorPolicyDTO?.make && agentUuid) {
            getModels(agentUuid, policyData?.motorPolicyDTO?.make).then(
                (res) => {
                    if (res && res.length) {
                        setModelOptions(
                            res.map((model) => ({
                                id: model.value,
                                name: model.label,
                            }))
                        );
                        setVariantOptions([]);
                    }
                    // setIsFetched((prev) => ({ ...prev, model: true }));
                }
            );
        }
    }, [policyData?.motorPolicyDTO?.make]);

    useEffect(() => {
        if (policyData?.motorPolicyDTO?.model && agentUuid) {
            getVariants(agentUuid, policyData?.motorPolicyDTO?.model).then(
                (res) => {
                    if (res && res.length) {
                        setVariantOptions(
                            res.map((variant) => ({
                                id: variant.value,
                                name: variant.label,
                            }))
                        );
                    }
                    // setIsFetched((prev) => ({ ...prev, variant: true }));
                }
            );
        }
    }, [policyData?.motorPolicyDTO?.model]);

    const handleSubmit = (values: FormValues) => {
        if (!customer || !customer.value) {
            console.error("Lead UUID is not available");
            return;
        }

        if (!values.policyType) return;

        let payload: PolicySavePayloadDTO = {
            ...policyData,
            client: {
                label: customer?.label,
                value: customer?.value,
            },
            flow: 3,
            businessType: 1,
            insuranceCategory: values.insuranceCategory,
            policyType: values.policyType,
            insurer: values.insurer,
            policyId: values.policyId,
            startDate: values.startDate ? values.startDate.toISOString() : "",
            endDate: values.endDate ? values.endDate.toISOString() : "",
            tpExpiryDate: values.tpExpiryDate
                ? values.tpExpiryDate.toISOString()
                : "",
            motorPolicyDTO: {
                ...policyData?.motorPolicyDTO,
                make: values.make,
                model: values.model,
                commercialType:
                    values.policyType === "MOTORCOMMERCIAL"
                        ? values.commercialType
                        : undefined,
                idv: values.idv ? values.idv : undefined,
                registrationNumber: values.registrationNumber,
            },
            netPremium: values.netPremium || 0,
            gstPremium: values.gstPremium || 0,
            policyPremium: values.policyPremium || 0,
            type: "policy",
        };
        onSubmit(payload);
    };
    return (
        <>
            <Formik
                // @ts-ignore
                ref={formikRef}
                initialValues={{
                    insuranceCategory: "Motor",
                    policyType: policyData?.policyType
                        ? MotorPolicyOptions.find(
                              (option) => option.id === policyData?.policyType
                          )?.id
                        : undefined,
                    insurer: policyData?.insurer,
                    startDate: policyData?.startDate
                        ? new Date(policyData?.startDate)
                        : null,
                    endDate: policyData?.endDate
                        ? new Date(policyData?.endDate)
                        : null,
                    tpExpiryDate: policyData?.tpExpiryDate
                        ? new Date(policyData?.tpExpiryDate)
                        : null,
                    commercialType: policyData?.motorPolicyDTO?.commercialType,
                    policyId: policyData?.policyId,
                    netPremium: policyData?.netPremium,
                    gstPremium: policyData?.gstPremium,
                    policyPremium: policyData?.policyPremium,
                    idv: policyData?.motorPolicyDTO?.idv || 0,
                    make: policyData?.motorPolicyDTO?.make || "",
                    model: policyData?.motorPolicyDTO?.model || "",
                    registrationNumber:
                        policyData?.motorPolicyDTO?.registrationNumber || "",
                }}
                validationSchema={getValidationSchema().shape({
                    tpExpiryDate: Yup.date().nullable(),
                    commercialType: Yup.string().when("policyType", {
                        is: (policyType: string) =>
                            policyType === "MOTORCOMMERCIAL",
                        then: Yup.string().required(
                            "Commercial type is required"
                        ),
                        otherwise: Yup.string().notRequired(),
                    }),
                    make: Yup.string().required("Make is required"),
                    model: Yup.string().required("Model is required"),
                    registrationNumber: Yup.string().required(
                        "Registration number is required"
                    ),
                    idv: Yup.number(),
                })}
                onSubmit={(values) => {
                    handleSubmit(values);
                }}
            >
                {({ isSubmitting, errors, values }) => {
                    console.log("values:", values);
                    console.log("errors", errors);

                    return (
                        <>
                            <Form>
                                <Box
                                    sx={{
                                        minHeight: { xs: "100%", sm: "456px" },
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Basic policy details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Insurance Category"
                                                name="insuranceCategory"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={"Motor"}
                                                items={[
                                                    {
                                                        id: "Motor",
                                                        name: "Motor",
                                                    },
                                                ]}
                                                change={() => {}}
                                                disabled={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Policy Type"
                                                name="policyType"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={
                                                    policyData?.policyType
                                                }
                                                items={MotorPolicyOptions}
                                                change={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="insurer"
                                                name="insurer"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={policyData?.insurer}
                                                items={
                                                    isFetched.insurer &&
                                                    insurerOptions.length
                                                        ? insurerOptions
                                                        : policyData?.insurer
                                                        ? [
                                                              {
                                                                  id: policyData?.insurer,
                                                                  name: policyData?.insurer,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                change={() => {}}
                                                onOpen={() => {
                                                    fetchInsurerOptions();
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Start date *"
                                                name="startDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="End date *"
                                                name="endDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="TP Expiry date"
                                                name="tpExpiryDate"
                                                type="date"
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Policy number"
                                                name="policyId"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Vehicle details
                                            </Typography>
                                        </Grid>
                                        {values.policyType ===
                                            "MOTORCOMMERCIAL" && (
                                            <Grid item xs={12} sm={6}>
                                                <FormikDropdown
                                                    title="Vehicle type *"
                                                    name="commercialType"
                                                    className=" dropDownSuper colorUpdate"
                                                    defaultVal={
                                                        policyData
                                                            ?.motorPolicyDTO
                                                            ?.commercialType ||
                                                        ""
                                                    }
                                                    items={[
                                                        {
                                                            name: "Goods carrying",
                                                            id: 1,
                                                        },
                                                        {
                                                            name: "Passenger carrying",
                                                            id: 2,
                                                        },
                                                        {
                                                            name: "Miscellaneous",
                                                            id: 4,
                                                        },
                                                    ]}
                                                    change={() => {}}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Make"
                                                name="make"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={
                                                    policyData?.motorPolicyDTO
                                                        ?.make || ""
                                                }
                                                items={
                                                    makeOptions.length > 0
                                                        ? makeOptions
                                                        : policyData
                                                              ?.motorPolicyDTO
                                                              ?.make
                                                        ? [
                                                              {
                                                                  id: policyData
                                                                      .motorPolicyDTO
                                                                      ?.make,
                                                                  name: policyData
                                                                      .motorPolicyDTO
                                                                      ?.make,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                change={(value: string) => {
                                                    getModels(
                                                        agentUuid,
                                                        value
                                                    ).then((res) => {
                                                        if (res && res.length) {
                                                            setModelOptions(
                                                                res.map(
                                                                    (
                                                                        model
                                                                    ) => ({
                                                                        id: model.value,
                                                                        name: model.label,
                                                                    })
                                                                )
                                                            );
                                                            setVariantOptions(
                                                                []
                                                            );
                                                            // setIsFetched(
                                                            //     (prev) => ({
                                                            //         ...prev,
                                                            //         model: true,
                                                            //     })
                                                            // );
                                                        }
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormikDropdown
                                                title="Model"
                                                name="model"
                                                className=" dropDownSuper colorUpdate"
                                                defaultVal={
                                                    policyData?.motorPolicyDTO
                                                        ?.model || ""
                                                }
                                                items={
                                                    modelOptions.length > 0
                                                        ? modelOptions
                                                        : policyData
                                                              ?.motorPolicyDTO
                                                              ?.model
                                                        ? [
                                                              {
                                                                  id:
                                                                      policyData
                                                                          .motorPolicyDTO
                                                                          .model ||
                                                                      "",
                                                                  name: policyData
                                                                      .motorPolicyDTO
                                                                      .model,
                                                              },
                                                          ]
                                                        : []
                                                }
                                                change={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Registration number *"
                                                name="registrationNumber"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="IDV"
                                                name="idv"
                                                type="number"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1">
                                                Premium details
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Net premium *"
                                                name="netPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="GST amount"
                                                name="gstPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MyText
                                                label="Total premium"
                                                name="policyPremium"
                                                type="text"
                                                className=""
                                                onChange={() => {}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider
                                    variant="dashed"
                                    sx={{
                                        borderColor: "grey.400",
                                        opacity: 0.3,
                                        my: "16px",
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                    }}
                                >
                                    <LoadingButton
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            marginLeft: { xs: 0, sm: "auot" },
                                            alignSelf: {
                                                xs: "center",
                                                sm: "flex-end",
                                            },
                                            background:
                                                theme.palette.brand.main,
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                        loading={isLoading}
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Submit
                                    </LoadingButton>
                                </Box>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </>
    );
}
